import React, { useEffect, useState } from 'react'

interface StepperProps {
  currentStep: number
  totalSteps: number
  stepProgress: {
    step1: number
    step2: number
    step3: number
    step4: number
    step5: number
  }
  onStepClick?: (step: number) => void
}

const Stepper: React.FC<StepperProps> = ({
  currentStep,
  totalSteps,
  stepProgress,
  onStepClick,
}) => {
  // Initialize previousProgress with the incoming prop to prevent animation on refresh.
  const [previousProgress, setPreviousProgress] = useState(stepProgress)
  const [animatingStep, setAnimatingStep] = useState<number | null>(null)

  const steps = [
    { number: 1, title: 'Start with your experience' },
    { number: 2, title: 'Add key activity information' },
    { number: 3, title: 'Provide activity overview' },
    { number: 4, title: 'Add package details' },
    { number: 5, title: 'Add booking options' },
  ]

  // Check for progress changes and trigger animation
  useEffect(() => {
    const currentStepKey = `step${currentStep}` as keyof typeof stepProgress
    const currentProgress = stepProgress[currentStepKey]
    const previousStepProgress = previousProgress[currentStepKey]

    if (currentProgress !== previousStepProgress) {
      setAnimatingStep(currentStep)

      // Clear animation after duration
      const timer = setTimeout(() => {
        setAnimatingStep(null)
      }, 800)

      setPreviousProgress(stepProgress)

      return () => clearTimeout(timer)
    }
  }, [stepProgress, currentStep, previousProgress])

  const handleStepClick = (stepNumber: number) => {
    if (onStepClick) {
      const canClick =
        stepNumber <= currentStep ||
        stepProgress[`step${stepNumber}` as keyof typeof stepProgress] > 0
      if (canClick) {
        onStepClick(stepNumber)
      }
    }
  }

  // Calculate overall progress
  const getOverallProgress = () => {
    const totalProgress = Object.values(stepProgress).reduce((sum, p) => sum + p, 0)
    return Math.round(totalProgress / totalSteps)
  }

  return (
    <div
      style={{
        marginBottom: '20px',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {/* Step indicator text */}
      <div style={{ marginBottom: '12px' }}>
        <span
          style={{
            fontSize: '14px',
            color: '#667085',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          STEP {currentStep} OF {totalSteps}
        </span>
        <span
          style={{
            fontSize: '12px',
            color: '#667085',
            marginLeft: '12px',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          ({getOverallProgress()}% complete)
        </span>
      </div>

      {/* Step title */}
      <p
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#212427',
          marginBottom: '20px',
          marginTop: '8px',
          lineHeight: '1.3',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {steps[currentStep - 1]?.title}
      </p>

      {/* Progress bars */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNum = index + 1
          const stepKey = `step${stepNum}` as keyof typeof stepProgress
          const progressPercent = stepProgress[stepKey] || 0
          const isActive = stepNum === currentStep
          const isAnimating = animatingStep === stepNum
          // const canClick = stepNum <= currentStep || progressPercent > 0
          const canClick = false

          return (
            <div
              key={stepNum}
              onClick={() => canClick && handleStepClick(stepNum)}
              style={{
                flex: 1,
                height: '6px',
                borderRadius: '3px',
                backgroundColor: '#D6DAE0',
                position: 'relative',
                overflow: 'hidden',
                cursor: canClick ? 'default' : 'default',
                transform: isActive ? 'scaleY(1.4)' : 'scaleY(1)',
                transition: 'transform 0.3s ease',
                boxShadow: isActive ? '0 2px 8px rgba(0, 204, 204, 0.3)' : 'none',
              }}
            >
              {/* Progress fill */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${progressPercent}%`,
                  backgroundColor: progressPercent > 0 ? '#00CCCC' : '#D6DAE0',
                  borderRadius: '3px',
                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: progressPercent > 0 ? '0 0 4px rgba(0, 72, 179, 0.4)' : 'none',
                }}
              />

              {/* Shimmer effect only when animating and progressing */}
              {isAnimating && progressPercent > 0 && progressPercent < 100 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '20px',
                    animation: 'shimmer 0.8s ease-out',
                    transform: `translateX(${Math.max(0, (progressPercent / 100) * 100 - 20)}px)`,
                  }}
                />
              )}
            </div>
          )
        })}
      </div>

      <style>
        {`
          @keyframes shimmer {
            0% {
              transform: translateX(-20px);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(100px);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  )
}

export default Stepper
